/**
 * useRole — Centralised role detection hook.
 *
 * BUG-2026-076: AppHeader, AppFooter, and LandingPage were each independently
 * probing 2-3 endpoints to determine the user's role, causing 7-9 API calls
 * per navigation. This hook probes once per token and shares the result via
 * module-level cache.
 *
 * Role hierarchy (highest to lowest):
 *   admin       — can access /@polyglot/contributors (Manage Users)
 *   editor      — can access /@qms/dashboard (Access QMS)
 *   contributor — has profile with training_completed_at
 *   user        — authenticated but none of the above
 *   public      — not authenticated
 *
 * Returns { role, profile, loading }.
 */
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getApiPath, fetchApi } from '../api';

/** Module-level cache: shared across all components calling useRole(). */
let cachedToken = null;
let cachedResult = null;   // { role, profile }
let inflightPromise = null;

function isTokenValid(token) {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return false;
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function probeRole() {
  const api = getApiPath();
  const checkAdmin = fetchApi(`${api}/@polyglot/contributors`).then(() => true).catch(() => false);
  const checkQms = fetchApi(`${api}/@qms/dashboard`).then(() => true).catch(() => false);
  const checkProfile = fetchApi(`${api}/@polyglot/contributors/me`).then((data) => data).catch(() => null);

  return Promise.all([checkAdmin, checkQms, checkProfile]).then(([isAdmin, hasQms, profile]) => {
    let role;
    if (isAdmin) {
      role = 'admin';
    } else if (hasQms) {
      role = 'editor';
    } else if (profile?.training_completed_at) {
      role = 'contributor';
    } else {
      role = 'user';
    }
    return { role, profile };
  });
}

export function useRole() {
  const token = useSelector((state) => state.userSession?.token);
  const loggedIn = isTokenValid(token);

  const [state, setState] = useState(() => {
    // If we already have a cached result for this token, use it immediately
    if (!loggedIn && cachedToken === null && cachedResult?.role === 'public') {
      return { role: 'public', profile: null, loading: false };
    }
    if (loggedIn && cachedToken === token && cachedResult) {
      return { ...cachedResult, loading: false };
    }
    return { role: loggedIn ? 'loading' : 'public', profile: null, loading: loggedIn };
  });

  useEffect(() => {
    if (!loggedIn) {
      cachedToken = null;
      cachedResult = { role: 'public', profile: null };
      inflightPromise = null;
      setState({ role: 'public', profile: null, loading: false });
      return;
    }

    // Already cached for this exact token
    if (cachedToken === token && cachedResult) {
      setState({ ...cachedResult, loading: false });
      return;
    }

    // Another component already started the probe for this token — reuse it
    if (cachedToken === token && inflightPromise) {
      inflightPromise.then((result) => {
        setState({ ...result, loading: false });
      });
      return;
    }

    // New token — probe once, cache for all consumers
    cachedToken = token;
    cachedResult = null;
    inflightPromise = probeRole().then((result) => {
      cachedResult = result;
      inflightPromise = null;
      return result;
    });

    inflightPromise.then((result) => {
      setState({ ...result, loading: false });
    });
  }, [loggedIn, token]);

  return state;
}

/**
 * Clear the role cache. Call on logout to ensure stale role
 * doesn't flash on next login.
 */
export function clearRoleCache() {
  cachedToken = null;
  cachedResult = null;
  inflightPromise = null;
}
